import { formatUnits, parseUnits } from 'viem';

export const GWEI_BASE = 9;
export const GWEI_UNIT = 1e9;
export const ZERO = BigInt(0);
export const ONE = BigInt(GWEI_UNIT);

type Gweiable = string | number;

export const formatNumber = (a: number) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: GWEI_BASE,
    maximumFractionDigits: GWEI_BASE,
    useGrouping: false,
    roundingMode: 'trunc',
  }).format(a);

export const Gwei = {
  toGwei: (a: Gweiable) => {
    // Avoid formatting as a string if possible. Simple multiplication is almost 100x faster.
    if (typeof a === 'number') {
      const inGwei = a * GWEI_UNIT;
      if (
        inGwei < BigInt(Number.MAX_SAFE_INTEGER) &&
        inGwei > BigInt(Number.MIN_SAFE_INTEGER) &&
        Math.trunc(inGwei) === inGwei &&
        inGwei / GWEI_UNIT === a
      ) {
        return BigInt(a * GWEI_UNIT);
      }
    }

    return parseUnits(typeof a === 'number' ? formatNumber(a) : a, GWEI_BASE);
  },
  fromGwei: (x: bigint): number => {
    // Avoid formatting as a string if possible. Simple division is 3-5x faster.
    if (x < BigInt(Number.MAX_SAFE_INTEGER) && x > BigInt(Number.MIN_SAFE_INTEGER)) {
      return Number(x) / GWEI_UNIT;
    }
    return Number(Gwei.fmt(x));
  },
  fmt: (a: bigint) => formatUnits(a, GWEI_BASE),
  min: (a: bigint, b: bigint) => (a > b ? b : a),
  max: (a: bigint, b: bigint) => (a > b ? a : b),
  div: (a: bigint, b: bigint) => (a * BigInt(GWEI_UNIT)) / b,
  mul: (a: bigint, b: bigint) => (a * b) / BigInt(GWEI_UNIT),
  abs: (a: bigint | number) => BigInt(a > 0 ? a : -a),
  floor: (a: bigint, precision: bigint) => (a / precision) * precision,
  ceil: (a: bigint, precision: bigint) => ((a + precision - 1n) / precision) * precision,
  flipSide: (a: bigint) => -a,
  hasDecimals: (a: bigint) => a % BigInt(GWEI_UNIT) !== 0n,
};

export const TokenHelper = {
  // Convert a token balance to Gwei, considering the token's decimals
  toGwei: (amount: bigint, tokenDecimals: number): bigint => {
    return (amount * BigInt(10 ** GWEI_BASE)) / BigInt(10 ** tokenDecimals);
  },

  // Convert a Gwei amount back to the token's decimal scale
  fromGwei: (amount: bigint, tokenDecimals: number): bigint => {
    return (amount * BigInt(10 ** tokenDecimals)) / BigInt(10 ** GWEI_BASE);
  },
};
